import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  MISSION_CATEGORIES,
  FOUNDING_COMPANIES,
  BADGE_DEFINITIONS,
} from "./data/missions";

const WA_NUMBER = "5213324922786";
const CLAIMS_PER_LEVEL_DEFAULT = 5;

// Deriva nivel, % de barra y texto a partir de cuántos han reclamado la
// misión, para que quede claro qué falta para el siguiente nivel sin que
// nadie tenga que calcularlo a mano en missions.js.
function getLevelInfo(mission) {
  if (mission.status === "completed") {
    return { levelLabel: mission.levelLabel, progressPct: mission.progressPct };
  }

  const perLevel = mission.claimsPerLevel || CLAIMS_PER_LEVEL_DEFAULT;
  const totalClaims = mission.firstClaim
    ? 1 + (mission.extraClaims?.length || 0)
    : 0;

  if (totalClaims === 0) {
    return { levelLabel: "Aún nadie la ha reclamado", progressPct: 0 };
  }

  const level = Math.ceil(totalClaims / perLevel);
  const claimsIntoLevel = totalClaims - (level - 1) * perLevel;
  const progressPct = Math.round((claimsIntoLevel / perLevel) * 100);
  const levelLabel = `Nivel ${level} · ${claimsIntoLevel}/${perLevel} para subir de nivel`;

  return { levelLabel, progressPct };
}

function MissionLevelBar({ label, pct }) {
  return (
    <div className="mj-stat mission-card__bar">
      <div className="mj-stat__header">
        <span className="mj-stat__label">{label}</span>
        <span className="mj-stat__value">{pct}%</span>
      </div>
      <div className="mj-stat__track">
        <div className="mj-stat__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MissionCard({ mission }) {
  const {
    icon,
    title,
    subtitle,
    status,
    firstClaim,
    extraClaims,
    extraLabel,
    claimCta,
  } = mission;

  if (status === "locked") {
    return (
      <div className="mission-card mission-card--locked">
        <span className="mission-card__icon">🔒</span>
        <h3 className="mission-card__title">{title}</h3>
        <p className="mission-card__subtitle">{subtitle}</p>
      </div>
    );
  }

  const waHref = claimCta
    ? `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(claimCta.text)}`
    : null;

  const { levelLabel, progressPct } = getLevelInfo(mission);

  return (
    <div
      className={`mission-card${status === "completed" ? " mission-card--completed" : ""}`}
    >
      <span className="mission-card__icon">{icon}</span>
      <h3 className="mission-card__title">{title}</h3>
      <p className="mission-card__subtitle">{subtitle}</p>

      <MissionLevelBar label={levelLabel} pct={progressPct} />

      {firstClaim ? (
        <div className="mission-card__claim">
          <span className="mission-card__claim-badge">
            {status === "completed" ? "✅ Misión completada" : "🏆 Primero en lograrlo"}
          </span>
          {firstClaim.message && (
            <p className="mission-card__claim-message">“{firstClaim.message}”</p>
          )}
          {firstClaim.note && (
            <p className="mission-card__claim-note">{firstClaim.note}</p>
          )}
          <p className="mission-card__claim-name">
            —{" "}
            {firstClaim.link ? (
              <a href={firstClaim.link} target="_blank" rel="noreferrer">
                {firstClaim.name}
              </a>
            ) : (
              firstClaim.name
            )}
          </p>
        </div>
      ) : (
        <p className="mission-card__empty">
          Nadie la ha reclamado todavía. ¡Sé el primero!
        </p>
      )}

      {extraClaims && extraClaims.length > 0 && (
        <p className="mission-card__extra">
          +{extraClaims.length} {extraLabel}
        </p>
      )}

      {waHref && (
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          className="btn btn-outline-light btn-sm mission-card__cta"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
          {firstClaim ? "Sumar mi apoyo" : "Reclamar esta misión"}
        </a>
      )}
    </div>
  );
}

function FoundersWall() {
  if (FOUNDING_COMPANIES.length === 0) return null;

  return (
    <div className="mission-founders">
      <h3 className="mission-founders__title">Salón de Empresas Fundadoras</h3>
      <div className="mission-founders__grid">
        {FOUNDING_COMPANIES.map((company) => (
          <div key={company.name} className="mission-founders__card">
            {company.logo ? (
              <img
                src={company.logo}
                alt={company.name}
                className="mission-founders__logo"
              />
            ) : (
              <div className="mission-founders__logo mission-founders__logo--placeholder">
                {company.name.charAt(0)}
              </div>
            )}
            <p className="mission-founders__name">
              {company.link ? (
                <a href={company.link} target="_blank" rel="noreferrer">
                  {company.name}
                </a>
              ) : (
                company.name
              )}
            </p>
            <div className="mission-founders__badges">
              {company.badges.map((badgeId) => {
                const badge = BADGE_DEFINITIONS[badgeId];
                if (!badge) return null;
                return (
                  <span
                    key={badgeId}
                    className="mission-badge-chip"
                    title={badge.description}
                  >
                    {badge.emoji} {badge.label}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MissionsSection() {
  return (
    <section id="misiones" className="mjp-section mjp-section--green">
      <div className="container-xl">
        <h2 className="section-title section-title--light text-center">
          Misiones 2026
        </h2>
        <p className="section-subtitle text-center mjp-subtitle--dim mission-intro">
          Como una mesa de regalos, pero de misiones: cada logro que
          completas —tú o tu empresa— ayuda a construir Modo Juego. Quien
          complete primero una misión deja su mensaje aquí para siempre; los
          que se suman después hacen subir el nivel.
        </p>

        <div className="mission-map">
          {MISSION_CATEGORIES.map((mission, i) => (
            <div
              key={mission.id}
              className={`mission-map__step mission-map__step--${i % 2 === 0 ? "left" : "right"}`}
            >
              <span
                className={`mission-map__dot${mission.status === "completed" ? " mission-map__dot--completed" : ""}`}
              />
              <MissionCard mission={mission} />
            </div>
          ))}
        </div>

        <FoundersWall />
      </div>
    </section>
  );
}
